import './Sidebar.css'
import NavLink from './NavLink'
import { ReactComponent as Logo } from '../assets/logo.svg'
import { Link, NavLinkProps, useNavigate } from 'react-router'
import { PropsWithChildren, ReactNode, useContext, useState } from 'react'
import Button from './Button'
import { ChevronDownIcon, LogoutIcon, MenuIcon, MoonIcon, SettingsIcon, SparklesIcon, SunIcon, WorldIcon } from './icons'
import clsx from 'clsx'
import Menu, { MenuItem } from './Menu'
import { AdminContext, OrganizationContext, ProjectContext } from '../contexts'
import { PreferencesContext } from './PreferencesContext'
import api from '../api'
import { combine, snakeToTitle } from '../utils'
import Modal from './Modal'
import { SingleSelect } from './form/SingleSelect'
import { useTranslation } from 'react-i18next'
import { ProfileImage } from './ProfileImage'
import WhatsNew from '../views/whatsnew/WhatsNew'
import { useReleaseNotes } from '../views/whatsnew/releaseNotes'

export interface SidebarLink extends NavLinkProps {
    key: string
    icon: ReactNode
}

interface SidebarProps {
    links?: SidebarLink[]
    prepend?: ReactNode
    append?: ReactNode
}

export default function Sidebar({ children, links, prepend, append }: PropsWithChildren<SidebarProps>) {
    const { t, i18n } = useTranslation()
    const profile = useContext(AdminContext)
    const [project] = useContext(ProjectContext)
    const [organization] = useContext(OrganizationContext)
    const navigate = useNavigate()
    const [preferences, setPreferences] = useContext(PreferencesContext)
    const [isOpen, setIsOpen] = useState(false)
    const [isLanguageOpen, setIsLanguageOpen] = useState(false)
    const [isWhatsNewOpen, setIsWhatsNewOpen] = useState(false)
    const { notes, hasUnread, markSeen } = useReleaseNotes()

    const openWhatsNew = () => {
        setIsWhatsNewOpen(true)
        markSeen()
    }

    const toggleTheme = () => {
        setPreferences({ ...preferences, mode: preferences.mode === 'dark' ? 'light' : 'dark' })
    }

    return (
        <>
            <header className="header">
                <Button onClick={() => setIsOpen(!isOpen)} icon={<MenuIcon />} aria-label="Menu" variant="secondary" size="small"/>
                <Link className="logo" to="/">
                    <Logo />
                </Link>
            </header>
            <section className={clsx('sidebar', { 'is-open': isOpen })}>
                <div className="sidebar-header">
                    <Link className="logo" to="/">
                        <Logo />
                    </Link>
                </div>
                {prepend}
                <nav>
                    {
                        links?.map(({ key, ...props }) => (
                            <NavLink {...props} key={key} onClick={() => setIsOpen(false)} />
                        ))
                    }
                </nav>
                {append}
                {profile && <div className="sidebar-profile">
                    <Menu button={
                        <div className="sidebar-profile-inner">
                            <ProfileImage admin={profile} />
                            <span className="profile-name">
                                {
                                    profile.first_name
                                        ? combine(profile.first_name, profile.last_name)
                                        : 'User'
                                }
                            </span>
                            <div className="profile-role">{snakeToTitle(project.role ?? organization.username)}</div>
                            <div className="profile-caret">
                                <ChevronDownIcon />
                            </div>
                            {hasUnread && <span className="whats-new-dot" aria-label={t('whats_new')} />}
                        </div>
                    }>
                        <MenuItem
                            icon={<SparklesIcon />}
                            onClick={openWhatsNew}>
                            <span className="whats-new-menu-item">
                                {t('whats_new')}
                                {hasUnread && <span className="whats-new-dot" aria-hidden="true" />}
                            </span>
                        </MenuItem>
                        <MenuItem
                            icon={<SettingsIcon />}
                            onClick={async () => {
                                await navigate('/organization')
                            }}>
                            {t('settings')}
                        </MenuItem>
                        <MenuItem
                            icon={<WorldIcon />}
                            onClick={() => setIsLanguageOpen(true)}>
                            {t('language')}
                        </MenuItem>
                        <MenuItem
                            icon={preferences.mode === 'dark' ? <SunIcon /> : <MoonIcon />}
                            onClick={() => toggleTheme()}>
                            {preferences.mode === 'dark'
                                ? t('light_mode')
                                : t('dark_mode')}
                        </MenuItem>
                        <MenuItem
                            icon={<LogoutIcon />}
                            onClick={async () => await api.auth.logout()}>
                            {t('sign_out')}
                        </MenuItem>
                    </Menu>
                </div>}
            </section>
            <main className={clsx({ 'is-open': isOpen })}>
                {children}
            </main>

            <WhatsNew open={isWhatsNewOpen} onClose={() => setIsWhatsNewOpen(false)} notes={notes} />

            <Modal open={isLanguageOpen} onClose={() => setIsLanguageOpen(false)} title={t('language')}>
                <SingleSelect
                    label={t('language')}
                    options={[
                        { label: 'English', key: 'en' },
                        { label: 'Español', key: 'es' },
                        { label: 'Français', key: 'fr' },
                        { label: 'Deutsch', key: 'de' },
                        { label: 'Italiano', key: 'it' },
                        { label: 'Português', key: 'pt' },
                    ]}
                    value={i18n.language}
                    getOptionDisplay={(option) => option.label}
                    toValue={(option) => option.key}
                    onChange={(value) => {
                        setPreferences({ ...preferences, lang: value })
                    }}
                />
            </Modal>
        </>
    )
}
